// @file Surface settings store
import { ALERT_ICON, CONFIRM_CANCEL_UPLOAD_SETTING, CONFIRM_DISCARD_SETTING_CHANGES } from '@@/bits/confirmation_dialog'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import { VizBackgroundStorage } from '@@/bits/viz_background_storage'
import { WishArrangementPreserver } from '@@/bits/wish_arrangement_preserver'
import { WallWishGroupByTypes } from '@@/enums'
import {
    OzConfirmationDialogBoxButtonScheme,
    useGlobalConfirmationDialogStore
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { WallApi } from '@@/surface/api/wall'
import type {
    User,
    Wall,
    WallAttributionSettings,
    WallBackground,
    WallLayoutAttributes,
    WallViz,
    WallWishGroupBy,
    WishArrangement
} from '@@/types'
import { SettingsSubpanel } from '@@/vuexstore/modules/surface_settings'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import type { ContentModeration, ContentProtection, WallBackgroundType } from '@padlet/arvo'
import type { BeethovenLuminance } from '@padlet/beethoven-client/src/types'
import { cloneDeep, isEqual } from 'es-toolkit'
import { find, isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useSurfaceSettingsStore = defineStore('surfaceSettings', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const globalSnackbarStore = useGlobalSnackbarStore()

  // Access the surface store in a getters to avoid circular dependency.
  // https://pinia.vuejs.org/cookbook/composing-stores.html
  const surfaceStore = useSurfaceStore()
  const wallAttributes = computed<Partial<Wall>>(() => surfaceStore.wallAttributes)
  const surfaceShareLinksStore = useSurfaceShareLinksStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

  // state
  const isTitleClicked = ref(false)
  const isDescriptionClicked = ref(false)
  const previousViz = ref<WallViz | null>(null)

  // getters
  const isRemakeable = computed<boolean>(() => surfaceVuexStore?.getters.isRemakeable)
  const isReactable = computed<boolean>(() => surfaceVuexStore?.getters.isReactable)
  const isCommentable = computed<boolean>(() => surfaceVuexStore?.getters.isCommentable)
  const isPreviewing = computed<boolean>(() => surfaceVuexStore?.getters['settings/isPreviewing'])
  const wishGroupBy = computed<WallWishGroupBy>(() => surfaceVuexStore?.getters['settings/wishGroupBy'])
  const isWishGroupedBySection = computed<boolean>(() => wishGroupBy.value === WallWishGroupByTypes.Section)
  const activePanel = computed<SettingsSubpanel | null>(() => surfaceVuexStore?.getters['settings/activePanel'])
  const isOpen = computed<boolean>(() => activePanel.value !== null)
  const wishSortBy = computed(() => surfaceVuexStore?.getters['settings/wishSortBy'])
  const previewAttributes = computed<Partial<Wall>>(() => surfaceVuexStore?.getters['settings/previewAttributes'])

  const hasUserSettingsChanged = computed(() => {
    const { background: currentBackground, ...previewWallAttributesMinusBackground } = previewAttributes?.value || {}
    const { background: originalBackground, ...originalWallAttributesMinusBackground } = wallAttributes?.value || {}

    // when opening a subpanel without the main panel e.g. recording limits
    // the states in such panels do not rely on preview wall attributes, and  startWallSettingsPreview won't be called to initialize the attributes.
    if (isEqual(previewAttributes.value, {})) {
      return false
    }

    // we only need to check for url in background. we add attributes to selected background so it
    // will not have the exact same attributes as the wallpapers in the panel
    return (
      !isEqual(previewWallAttributesMinusBackground, originalWallAttributesMinusBackground) ||
      currentBackground?.url !== originalBackground?.url
    )
  })

  const isSavingSettings = computed<boolean>(() => surfaceVuexStore?.state.settings.isSaving || false)

  const showAuthor = computed<boolean>(() => previewAttributes?.value.show_author ?? surfaceStore.xAuthor)
  const attributionSettings = computed<WallAttributionSettings>(() => wallAttributes.value.attribution_settings ?? [])
  const showPostsAuthor = computed<boolean>(() => attributionSettings.value.includes('posts'))
  const showCommentsAuthor = computed<boolean>(() => attributionSettings.value.includes('comments'))
  const canConfigNewPostLocation = computed<boolean>(() => {
    return (
      surfaceStartingStateStore.startingState.constants?.newPostLocationConfigurableLayouts?.includes(
        surfaceStore.viz,
      ) ?? false
    )
  })

  const isWallNameValid = computed<boolean>(() => surfaceVuexStore?.state.settings.isWallNameValid || false)
  const isWallNameAvailable = computed<boolean>(() => surfaceVuexStore?.state.settings.isWallNameAvailable || false)
  const contentModeration = computed<ContentModeration>(() => surfaceVuexStore?.getters['settings/contentModeration'])
  const contentModerationOptions = computed<ContentModeration[]>(() => {
    if (surfaceStore.isUsedInEducationalContext) {
      return ['none', 'auto', 'students_only', 'all']
    }
    return ['none', 'auto', 'all']
  })
  const contentProtection = computed<ContentProtection>(() => surfaceVuexStore?.getters['settings/contentProtection'])

  // While migrating to columns, we want to disable sections for grid viz (columns must have sections)
  const shouldShowSectionsIfColumnsLayoutIsEnabled = computed(() => {
    return surfaceStore.viz !== 'grid' && surfaceStore.viz !== 'columns'
  })

  // #region wall layouts
  // In Shelf & old Timeline, the 'Group posts by section' field does not do anything so we hide the whole layout section.
  const canShowSectionLayoutSettings = computed(() => {
    if (!surfaceStore.canUseSections) return false
    if (isAppUsing('groupPostsV2') && surfaceStore.isShelf) return true
    if (surfaceStore.viz === 'shelf' || surfaceStore.viz === 'columns') return false
    return shouldShowSectionsIfColumnsLayoutIsEnabled.value
  })

  const orderedLayouts = computed<WallViz[]>(() => {
    return ['grid', 'shelf', 'matrix', 'table', 'free', 'timeline_v2', 'timeline', 'stream', 'map']
  })

  const layoutConversionOptions = computed<WallLayoutAttributes[]>(() => {
    const allLayoutsByGroup = surfaceStartingStateStore.allLayoutsByGroup
    return orderedLayouts.value.map((layout) =>
      allLayoutsByGroup.simple.find((l) => l.value === layout),
    ) as WallLayoutAttributes[]
  })

  const vizBackgroundStorage = new VizBackgroundStorage()
  const wishArrangementPreserver = new WishArrangementPreserver()
  // #endregion

  // actions
  function setIsTitleClicked(isClicked: boolean): void {
    isTitleClicked.value = isClicked
  }

  function setIsDescriptionClicked(isClicked: boolean): void {
    isDescriptionClicked.value = isClicked
  }

  function showConfirmDiscardSettingsDialog(): void {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...CONFIRM_DISCARD_SETTING_CHANGES,
      ...ALERT_ICON,
      afterDiscardActions: [hideSettingsPanelWithoutConfirmation],
      afterConfirmActions: [
        async () => await surfaceVuexStore?.dispatch('settings/saveSettings', null, { root: true }),
        hideSettingsPanelWithoutConfirmation,
      ],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
      forceFullWidthButtons: true,
      xShadow: true,
    })
  }

  function showConfirmCancelImageUploadSettingsDialog(
    { shouldHideSettingsPanel }: { shouldHideSettingsPanel: boolean } = { shouldHideSettingsPanel: true },
  ): void {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...CONFIRM_CANCEL_UPLOAD_SETTING,
      ...ALERT_ICON,
      afterConfirmActions: [
        async () =>
          shouldHideSettingsPanel
            ? hideSettingsPanelWithoutConfirmation()
            : await surfaceVuexStore?.dispatch('settings/changeActivePanel', SettingsSubpanel.Main, { root: true }),
      ],
      forceFullWidthButtons: true,
      xShadow: true,
    })
  }

  function toggleSettingsPanel(panel: SettingsSubpanel = SettingsSubpanel.Main): void {
    void surfaceVuexStore?.dispatch('settings/toggleSettingsPanel', panel)
  }

  function showSettingsPanel(activePanel: SettingsSubpanel = SettingsSubpanel.Main): void {
    void surfaceVuexStore?.dispatch('settings/showSettingsPanel', activePanel)
  }

  function hideSettingsPanel({ checkForUserSettings } = { checkForUserSettings: true }): void {
    if (checkForUserSettings && hasUserSettingsChanged.value) {
      showConfirmDiscardSettingsDialog()
    } else {
      hideSettingsPanelWithoutConfirmation()
    }
  }

  function hideSettingsPanelWithoutConfirmation(): void {
    void surfaceVuexStore?.dispatch('settings/hideSettingsPanel', null, { root: true })
  }

  function changeActivePanel(activePanel: SettingsSubpanel): void {
    void surfaceVuexStore?.dispatch('settings/changeActivePanel', activePanel)
  }

  function goBackToMainPanel(): void {
    void changeActivePanel(SettingsSubpanel.Main)
  }

  function updateBackground(background: Partial<WallBackground>, isCustom = false): void {
    let updatedBackground
    let updatedIsTitleBarTransparent

    if (isCustom) {
      updatedIsTitleBarTransparent = background.is_full_page
      updatedBackground = {
        ...background,
        effect: background.effects != null ? background.effects[0] : '',
        is_full_page: background.is_title_bar_transparent_by_default ?? false, // Default to false if it is a user selected image
        is_title_bar_transparent: updatedIsTitleBarTransparent,
        thumbnail_url: null, // remove thumbnail_url if it is a user selected image
      }
    } else {
      updatedIsTitleBarTransparent = true
      updatedBackground = {
        ...background,
        effect: '',
        effects: [],
        is_full_page: background.is_title_bar_transparent_by_default ?? true,
        is_title_bar_transparent: updatedIsTitleBarTransparent,
      }
    }

    updatePreviewAttributes({ background: updatedBackground, is_title_bar_transparent: updatedIsTitleBarTransparent })

    // Save the background choice for non-map viz layouts
    const wallHashid = wallAttributes.value.hashid
    const currentViz = surfaceStore.viz
    if (wallHashid && currentViz && typeof updatedBackground.url === 'string' && updatedBackground.url.length > 0) {
      vizBackgroundStorage.saveBackgroundForViz(wallHashid, currentViz, updatedBackground)
    }
  }

  function updateBackgroundMetadata(payload: {
    dominantColourAsRgb: string
    imageLuminance: BeethovenLuminance
  }): void {
    void surfaceVuexStore?.dispatch('settings/updateBackgroundMetadata', payload)
  }

  function startWallAttributesPreview(): void {
    void surfaceVuexStore?.dispatch('settings/startWallAttributesPreview')
  }

  function stopWallAttributesPreview(): void {
    void surfaceVuexStore?.dispatch('settings/stopWallAttributesPreview')
  }

  function updatePreviewAttributes(attr: Record<string, any>, skipPreviewMode: boolean = false): void {
    if (!skipPreviewMode && !isPreviewing.value) return
    const newPreviewAttributes: Partial<Wall> = { ...previewAttributes.value, ...attr }
    void surfaceVuexStore?.dispatch('settings/updateAndValidatePreviewAttributes', newPreviewAttributes)
  }

  function updateAndValidatePreviewAttributes(attr: Record<string, any>): void {
    void surfaceVuexStore?.dispatch('settings/updateAndValidatePreviewAttributes', attr)
  }

  function saveSettings(): void {
    void surfaceVuexStore?.dispatch('settings/updateWallValidity')

    void (async () => {
      const wallStateSnapshot = cloneDeep(surfaceStore.wallAttributes) // for rollback on error
      // optimistically update base wall state. otherwise user may briefly see oudated wall state should
      // settings panel be closed before the `saveSettings` API resolves.
      surfaceStore.updateWall(previewAttributes.value)

      const success = await surfaceVuexStore?.dispatch('settings/saveSettings')
      if (!success) {
        surfaceStore.updateWall(wallStateSnapshot)
        return
      }

      // We want to ensure that the wall settings (specifically the title and description) are updated on the server before we attempt to translate them.
      if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateWallEntity()
      }
    })()
  }

  /**
   * Use this instead of `saveSettings` when subsequent code depends on settings being saved before continuing.
   * Unlike saveSettings(), this does not optimistically update the UI - it waits for the API to complete
   * and the Vuex action will update the wall state with the server response.
   */
  async function asyncSaveSettings(): Promise<void> {
    await surfaceVuexStore?.dispatch('settings/updateWallValidity')
    const success = await surfaceVuexStore?.dispatch('settings/saveSettings')

    if (!success) {
      return
    }

    // We want to ensure that the wall settings (specifically the title and description) are updated on the server before we attempt to translate them.
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateWallEntity()
    }
  }

  function updatePreviewAttributesAndSaveSettings(attr: Record<string, any>, skipPreviewMode: boolean = false): void {
    updatePreviewAttributes(attr, skipPreviewMode)
    saveSettings()
  }

  /**
   * Use this instead of `updatePreviewAttributesAndSaveSettings` when subsequent code depends on settings being saved before continuing
   */
  async function asyncUpdatePreviewAttributesAndSaveSettings(
    attr: Record<string, any>,
    skipPreviewMode: boolean = false,
  ): Promise<void> {
    updatePreviewAttributes(attr, skipPreviewMode)
    await asyncSaveSettings()
  }

  function saveSettingsAndGoBackToMainPanel(): void {
    saveSettings()
    goBackToMainPanel()
  }

  function resetSettings(): void {
    updatePreviewAttributes({ ...wallAttributes.value })
  }

  function discardChangesAndGoBackToMainPanel(): void {
    resetSettings()
    goBackToMainPanel()
  }

  function notifyWallSettingsUpdate(currentUserId: number, actor?: User): void {
    if (actor === undefined || actor.id === currentUserId || activePanel.value === null) {
      return
    }

    const user = actor.name || actor.username
    const message = user != null ? __('%{user} edited settings', { user }) : __('Someone edited settings')

    if (!globalSnackbarStore.isMessageBeingShown(message)) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message,
      })
    }
  }

  async function toggleGroupBySection(skipPreviewMode: boolean = false): Promise<void> {
    const updatedValue = !isWishGroupedBySection.value
    const wishArrangement = {
      default_section_id: surfaceSectionsStore.defaultSectionId,
      group_by: updatedValue ? WallWishGroupByTypes.Section : null, // Set group_by to null for disabling grouping
      is_sectionable: updatedValue,
    }

    await asyncUpdatePreviewAttributesAndSaveSettings({
      wish_arrangement: wishArrangement,
      skipPreviewMode,
    })
  }

  /**
   * Converts the wall layout to a new format
   * @param newLayout The new layout format to convert to
   * @param isSectioned Whether sections are currently turned on or off for the wall
   * @param enableSections Whether to enable sections for the wall after converting
   */
  const convertWallLayout = async (
    newLayout: WallViz,
    { isSectioned, enableSections }: { isSectioned?: boolean; enableSections?: boolean } = {},
  ): Promise<void> => {
    const wallId = wallAttributes.value.id
    const wallHashid = wallAttributes.value.hashid
    if (wallHashid == null) {
      throw new Error('Wall hashid is required to convert wall layout')
    }

    // Store the current layout as previous layout before converting
    const currentViz = surfaceStore.viz
    if (currentViz && currentViz !== newLayout) {
      previousViz.value = currentViz
    }

    // Save current background for non-map layouts before converting
    const currentBackground = previewAttributes.value.background ?? wallAttributes.value.background
    if (currentViz && currentBackground?.url) {
      vizBackgroundStorage.saveBackgroundForViz(wallHashid, currentViz, currentBackground)
    }

    // Save current wish arrangement settings before converting
    const currentWishArrangement = previewAttributes.value.wish_arrangement ?? wallAttributes.value.wish_arrangement
    if (currentWishArrangement && wishArrangementPreserver.shouldSaveWishArrangement(currentViz, newLayout)) {
      wishArrangementPreserver.saveSettingsForViz(wallHashid, currentWishArrangement)
    }

    try {
      await WallApi.convertLayout({
        wallHashid,
        // if we set viz = 'grid' with sections turned on, we call the convertLayout API with newLayout = 'shelf' instead
        newLayout: newLayout === 'grid' && isSectioned === true ? 'shelf' : newLayout,
      })

      // Try to restore saved background for non-map layouts
      if (newLayout !== 'map') {
        const savedBackground = vizBackgroundStorage.getBackgroundForViz(wallHashid, newLayout)
        if (savedBackground) {
          updateBackground(savedBackground, savedBackground.is_custom)
        }
      }

      const attributes: Record<string, any> = {
        viz: newLayout,
      }

      let savedWishArrangement: WishArrangement | null = null
      // Try to restore saved wish arrangement settings for the new layout
      // Use dynamic import to avoid circular dependency with surface_post_properties
      try {
        const { useSurfacePostPropertiesStore } = await import('@@/pinia/surface_post_properties')
        const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

        if (
          wishArrangementPreserver.shouldRestoreWishArrangement(
            currentViz,
            newLayout,
            wallHashid,
            showAuthor.value,
            surfacePostPropertiesStore.wallCustomPostPropertiesById,
          )
        ) {
          savedWishArrangement = wishArrangementPreserver.getSettingsForViz(wallHashid)
        }
      } catch (e) {
        // If dynamic import fails, continue without restoring wish arrangement
        // This ensures the layout conversion still works even if post properties store is unavailable
        captureException(e, {
          context: `Failed to import surface_post_properties store during layout conversion for wall: ${wallId ?? ''}`,
        })
      }

      if (savedWishArrangement !== null) {
        attributes.wish_arrangement = savedWishArrangement
      } else if (enableSections === true && surfaceWishArrangementStore.isGroupByNone) {
        // Only update group by settings to be Section if the wall is group by None.
        // Otherwise, keep the current group by settings (e.g. keep Color, Author, etc.).
        attributes.wish_arrangement = {
          group_by: WallWishGroupByTypes.Section,
          is_sectionable: true,
        }
      } else if (enableSections === false) {
        attributes.wish_arrangement = {
          group_by: null,
          is_sectionable: false,
        }
      }

      /**
       * https://linear.app/padlet/issue/PRO-27405/sometimes-the-format-isnt-updated
       * There is a known issue where closing SurfaceSettingsMainPanel calls RESET_SETTINGS_STATE action, causing previewAttributes to be cleared.
       * This causes convertWallLayout to fail because the wall hashid in previewAttributes is no longer available when saveSettings() is called.
       *
       * Short-term fix: Use previewAttributes as base if it has content (preserves restored backgrounds, e.g., when converting from map to non-map),
       * otherwise fallback to wallAttributes to ensure hashid and required fields are present. Always explicitly include hashid as a safety net.
       * Tradeoff: Other unsaved preview attributes (e.g., edited title, description) will be lost when layout is converted.
       *
       * Long-term fix: We need to localize the previewAttributes to the panel responsible for it, so that each panel can manage its own preview state independently.
       */

      const baseAttributes = !isEmpty(previewAttributes.value) ? previewAttributes.value : wallAttributes.value

      updatePreviewAttributesAndSaveSettings(
        {
          ...baseAttributes,
          ...attributes,
        },
        true,
      )

      if (surfaceStore.canHaveConnections) {
        void useSurfacePostConnectionStore().fetchConnections()
      }

      /**
       * If switching to a non-sectionable format, we fetch
       * the updated breakout links as they're disabled on backend.
       */
      if (!surfaceStore.canUseSections) {
        void surfaceShareLinksStore.fetchSectionBreakoutLinks()
      }
    } catch (e) {
      captureException(e, {
        context: `Failed to convert wall layout for wall: ${wallId ?? ''} to ${newLayout}`,
      })
    }
  }

  // #region comments and reactions settings
  const isReactionDataValid = computed<boolean>(() => surfaceVuexStore?.state.settings.isReactionDataValid || false)

  function disableCommentsAndReactions(): void {
    void surfaceVuexStore?.dispatch('updateWall', { is_commentable: false, is_reactable: false })
  }

  async function fetchCommentAndReactionSettings(): Promise<void> {
    const settings = await WallApi.fetch(surfaceStore.wallHashid)
    void surfaceVuexStore?.dispatch('updateWall', {
      is_commentable: settings.is_commentable,
      is_reactable: settings.is_reactable,
    })
  }

  const isChangingReactionType = computed(() => {
    // This should cover all reaction type changes, including off => reaction or reaction => off
    // When reaction => off, wallAttributes.reaction_data stays unchanged
    // When off => reaction, we compare previewAttributes.reaction_data with wallAttributes.reaction_data, which can either be null or the last reaction setting
    const reactionData = wallAttributes.value.reaction_data
    const newReactionData = previewAttributes.value.reaction_data
    return reactionData?.type !== newReactionData?.type
  })
  // #endregion
  const isFetchingWallpapers = ref(false)
  const wallpapersGroupedByType = ref<Record<WallBackgroundType, WallBackground[]>>()
  const wallpaper = computed(() => {
    if (Boolean(surfaceStore.isCustomBackground) || wallpapersGroupedByType.value == null) {
      return previewAttributes.value.background
    }

    // if the background is an image from padlet airtable, we need to find the wallpaper object from the list of wallpapers
    // to get its thumbnail url. otherwise, we just use the background object from surface init state directly.
    const allWallpapers = Object.values(wallpapersGroupedByType.value).flat()
    return find(allWallpapers, { url: previewAttributes.value.background?.url }) ?? previewAttributes.value.background
  })

  const isUsingSolidPurpleLightWallpaper = computed(() => {
    return (
      wallpaper.value?.url ===
      'https://padlet.net/wallpapers/v0/a653a84b88fb16502b188e66b493a310/1aedf0eb/solidpurplelight.png'
    )
  })

  // #region Screen reader notifications
  const screenReaderNotificationsStore = useScreenReaderNotificationsStore()
  function setScreenReaderMessageBasedOnWallUpdate(latestUpdate: Partial<Wall>): void {
    const prevTitle = surfaceStore.wallAttributes.title
    const prevDescription = surfaceStore.wallAttributes.description
    if (prevTitle !== latestUpdate.title) {
      screenReaderNotificationsStore.addScreenReaderMessage(
        __('Padlet title changed from %{prevTitle} to %{newTitle}', { prevTitle, newTitle: latestUpdate.title }),
      )
    } else if (prevDescription !== latestUpdate.description) {
      screenReaderNotificationsStore.addScreenReaderMessage(
        __('Padlet description changed from %{prevDescription} to %{newDescription}', {
          prevDescription,
          newDescription: latestUpdate.description,
        }),
      )
    }
  }
  // #endregion

  return {
    // state
    isTitleClicked,
    isDescriptionClicked,
    isFetchingWallpapers,
    wallpapersGroupedByType,
    orderedLayouts,
    previousViz,

    // getters
    wallAttributes,
    isRemakeable,
    isReactable,
    isCommentable,
    isPreviewing,
    previewAttributes,
    wishGroupBy,
    isWishGroupedBySection,
    wishSortBy,
    activePanel,
    isOpen,
    isReactionDataValid,
    showAuthor,
    attributionSettings,
    showPostsAuthor,
    showCommentsAuthor,
    canConfigNewPostLocation,
    isSavingSettings,
    isWallNameValid,
    isWallNameAvailable,
    contentModeration,
    contentModerationOptions,
    contentProtection,
    layoutConversionOptions,
    canShowSectionLayoutSettings,
    wallpaper,
    isUsingSolidPurpleLightWallpaper,
    isChangingReactionType,

    // actions
    setIsTitleClicked,
    setIsDescriptionClicked,
    startWallAttributesPreview,
    stopWallAttributesPreview,
    toggleSettingsPanel,
    toggleGroupBySection,
    updatePreviewAttributes,
    updateAndValidatePreviewAttributes,
    updateBackground,
    updateBackgroundMetadata,
    convertWallLayout,
    changeActivePanel,
    goBackToMainPanel,
    showSettingsPanel,
    hideSettingsPanel,
    hideSettingsPanelWithoutConfirmation,
    saveSettings,
    asyncSaveSettings,
    saveSettingsAndGoBackToMainPanel,
    discardChangesAndGoBackToMainPanel,
    updatePreviewAttributesAndSaveSettings,
    asyncUpdatePreviewAttributesAndSaveSettings,
    notifyWallSettingsUpdate,
    showConfirmCancelImageUploadSettingsDialog,
    disableCommentsAndReactions,
    fetchCommentAndReactionSettings,
    setScreenReaderMessageBasedOnWallUpdate,
  }
})
export { useSurfaceSettingsStore }
